import { useCallback, useRef } from "react";

import { GenericObject } from "@vahak/core/dist/custom-types/generic-object";
import { GA4EventNames } from "@vahak/core/dist/constants/event-names";

import { COMPACT_AUTH_STEPS } from "../compact-auth";
import { COMPACT_LOAD_BIDDING_STEPS } from "../compact-load-bidding/types";
import { COMPACT_LOAD_POSTING_STEPS } from "../compact-load-posting/types";
import { TrackEventHandler } from "../../../types/events";

const WEB_V2 = GA4EventNames.WEB_V2;

/** Compact posting step → GA4 page-opened event name. */
export const POSTING_STEP_PAGE_OPENED_EVENT = {
    [COMPACT_LOAD_POSTING_STEPS.ROUTE_DETAILS]: WEB_V2.load_form_location_page_opened,
    [COMPACT_LOAD_POSTING_STEPS.LOAD_DETAILS]: WEB_V2.load_form_spec_page_opened,
    [COMPACT_LOAD_POSTING_STEPS.FREIGHT_QUOTE]: WEB_V2.load_form_price_page_opened
} as const;

export type WebV2FunnelEventKey =
    | typeof POSTING_STEP_PAGE_OPENED_EVENT[keyof typeof POSTING_STEP_PAGE_OPENED_EVENT]
    | typeof WEB_V2.login_sign_up_form_opened
    | typeof WEB_V2.bid_form_basic_opened
    | typeof WEB_V2.bid_form_detailed_opened;

export function useWebV2FunnelEventOnce(trackEvent?: TrackEventHandler, eventData?: GenericObject) {
    const firedRef = useRef<Partial<Record<WebV2FunnelEventKey, boolean>>>({});

    const trackOnce = useCallback(
        (event: WebV2FunnelEventKey, data?: GenericObject) => {
            if (firedRef.current[event]) {
                return;
            }
            firedRef.current[event] = true;
            trackEvent?.(event, { ...eventData, ...data });
        },
        [eventData, trackEvent]
    );

    const reset = useCallback(() => {
        firedRef.current = {};
    }, []);

    /** Re-emit a funnel page-open after auth when the user returns to a step already deduped by `trackOnce`. */
    const trackFunnelEventAfterAuth = useCallback(
        (event: WebV2FunnelEventKey, data?: GenericObject) => {
            delete firedRef.current[event];
            trackOnce(event, data);
        },
        [trackOnce]
    );

    return { trackOnce, trackFunnelEventAfterAuth, reset };
}

interface PostingFunnelStepContext {
    flow: "posting";
    step: COMPACT_LOAD_POSTING_STEPS;
    loginFormStep: COMPACT_AUTH_STEPS;
    routeSkippedOnOpen?: boolean;
}

interface BiddingFunnelStepContext {
    flow: "bidding";
    step: COMPACT_LOAD_BIDDING_STEPS;
    loginFormStep: COMPACT_AUTH_STEPS;
    showBookingSection: boolean;
}

/** Map compact flow step → WEB_V2 funnel page-open event. */
export function getWebV2FunnelEvent(
    context: PostingFunnelStepContext | BiddingFunnelStepContext
): WebV2FunnelEventKey | undefined {
    if (context.flow === "posting") {
        if (context.step === COMPACT_LOAD_POSTING_STEPS.LOGIN) {
            if (context.loginFormStep === COMPACT_AUTH_STEPS.PHONE) {
                return WEB_V2.login_sign_up_form_opened;
            }
            return undefined;
        }

        return context.step in POSTING_STEP_PAGE_OPENED_EVENT
            ? POSTING_STEP_PAGE_OPENED_EVENT[context.step as keyof typeof POSTING_STEP_PAGE_OPENED_EVENT]
            : undefined;
    }

    switch (context.step) {
        case COMPACT_LOAD_BIDDING_STEPS.BID_DETAILS:
            return context.showBookingSection ? WEB_V2.bid_form_detailed_opened : WEB_V2.bid_form_basic_opened;
        case COMPACT_LOAD_BIDDING_STEPS.LOGIN:
            if (context.loginFormStep === COMPACT_AUTH_STEPS.PHONE) {
                return WEB_V2.login_sign_up_form_opened;
            }
            return undefined;
        default:
            return undefined;
    }
}

export function useTrackWebV2FieldOnce(trackEvent?: TrackEventHandler, eventData?: GenericObject) {
    const firedRef = useRef<Record<string, boolean>>({});

    const trackFieldOnce = useCallback(
        (dedupeKey: string, eventName: string, data?: GenericObject) => {
            if (firedRef.current[dedupeKey]) {
                return;
            }
            firedRef.current[dedupeKey] = true;
            trackEvent?.(eventName, { ...eventData, ...data });
        },
        [eventData, trackEvent]
    );

    const reset = useCallback(() => {
        firedRef.current = {};
    }, []);

    return { trackFieldOnce, reset };
}
